<script lang="ts">
  import { onMount } from 'svelte';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { fetchWithAuth } from '$lib/utils/http/httpUtil';

  export let clientId: number;
  export let basePath: string;

  type ClientSetting = 'canSync' | 'canInvoicePost' | 'canExpenseClaimPost';
  type ClientSettings = Record<ClientSetting, boolean> & {
    hasConfiguredEntities: boolean;
  };

  const routes: Record<ClientSetting, string> = {
    canSync: 'sync',
    canInvoicePost: 'invoice-post',
    canExpenseClaimPost: 'expense-claim-post',
  };

  let settings: ClientSettings = {
    canSync: true,
    canInvoicePost: true,
    canExpenseClaimPost: true,
    hasConfiguredEntities: false,
  };
  let isLoading = false;
  let isUpdating = false;

  onMount(load);

  async function load() {
    isLoading = true;
    try {
      const response = await fetchWithAuth(`${basePath}/clients/${clientId}/settings`);
      if (response.ok) {
        settings = await response.json();
      } else {
        showToast({ title: 'Error', message: 'Failed to load client integration settings', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to load client integration settings', type: 'error' });
    } finally {
      isLoading = false;
    }
  }

  async function update(setting: ClientSetting) {
    isUpdating = true;
    try {
      const response = await fetchWithAuth(`${basePath}/clients/${clientId}/${routes[setting]}?isEnabled=${settings[setting]}`, { method: 'POST' });
      if (response.ok) {
        showToast({ title: 'Success', message: 'Client integration setting updated', type: 'success' });
      } else {
        showToast({ title: 'Error', message: 'Failed to update client integration setting', type: 'error' });
        await load();
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to update client integration setting', type: 'error' });
      await load();
    } finally {
      isUpdating = false;
    }
  }
</script>

{#if settings.hasConfiguredEntities}
  <section class="mb-4">
    <h4 class="mb-3 text-lg font-semibold text-gray-800">Client Sync Settings</h4>
    <div class="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
      These settings apply to all configured entities. To disable a setting for an individual entity, select that entity and update its settings.
    </div>
    <div class="flex flex-wrap gap-x-5 gap-y-2">
      <label class="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" bind:checked={settings.canSync} on:change={() => update('canSync')} class="rounded" disabled={isLoading || isUpdating} />
        Can Sync
      </label>
      <label class="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" bind:checked={settings.canInvoicePost} on:change={() => update('canInvoicePost')} class="rounded" disabled={isLoading || isUpdating} />
        Can Invoice Post
      </label>
      <label class="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" bind:checked={settings.canExpenseClaimPost} on:change={() => update('canExpenseClaimPost')} class="rounded" disabled={isLoading || isUpdating} />
        Can Expense Claim Post
      </label>
    </div>
  </section>
{/if}
